import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    Chip,
    CircularProgress,
    IconButton,
    Divider,
    Tooltip,
    Avatar,
} from "@mui/material";
import {
    CheckCircle,
    Warning,
    Cancel,
    Close,
    Edit as EditIcon,
    Undo,
    HelpOutline,
    ContentCopy,
    Inventory2Outlined,
} from "@mui/icons-material";
import { Customer, Product } from "../../types";
import { OrderImportItem, OrderImportPayload } from "../../types/orderImport";
import { OrderFormPrefill } from "../forms/OrderForm";
import { unitPriceFor } from "../../utils/productPricing";
import { ProductSearchAutocomplete } from "../common/ProductSearchAutocomplete";
import { CustomerSearchAutocomplete } from "../common/CustomerSearchAutocomplete";
import {
    ProductThumbnail,
    getProductThumbnailUrl,
} from "../common/ProductThumbnail";
import {
    OrderChannelSimple,
    orderChannelService,
} from "../../services/orderChannelService";
import {
    DeliveryServiceSimple,
    deliveryService,
} from "../../services/deliveryService";
import { PaymentMethod, PAYMENT_METHOD_LABELS } from "../../utils/paymentMethods";
import {
    CustomerResolution,
    ProductResolution,
    parseOrderImportPayload,
    resolveCustomer,
    resolveNamed,
    resolvePaymentMethod,
    resolveProduct,
} from "../../utils/orderImportResolver";

interface OrderImportDialogProps {
    open: boolean;
    onClose: () => void;
    // Hands the caller a fully-built OrderFormPrefill — the caller is
    // responsible for closing this dialog and opening OrderForm with it,
    // exactly like the guest-order-approval flow already does.
    onResolved: (prefill: OrderFormPrefill) => void;
}

interface ItemRowState {
    item: OrderImportItem;
    resolution: ProductResolution;
    selectedProduct: Product | null;
    skipped: boolean;
    // Toggles the inline search box for an already-matched item, in case
    // staff wants to override an auto-match.
    editing: boolean;
}

type PaymentPlan =
    | { kind: "none" }
    | {
          kind: "ready";
          method: PaymentMethod;
          amount: number;
          reference?: string;
      }
    | { kind: "skipped"; reason: string };

const SAMPLE_PLACEHOLDER = `{
  "customer": {
    "phone": "01712345678",
    "first_name": "Rima",
    "street_address": "House 12, Road 5, Dhanmondi",
    "city": "Dhaka",
    "map_link": "https://maps.app.goo.gl/xxxx",
    "profile": "https://facebook.com/rima.example"
  },
  "delivery_address": { "street_address": "Level 4, Green Tower, Gulshan 1", "city": "Dhaka" },
  "channel": "Facebook",
  "delivery_service": "Pathao",
  "items": [
    { "sku": "HSP-CRM-CKN-160", "customer_hint": "the chicken one, small pack", "quantity": 2 },
    { "product_name": "Cat toy mouse", "customer_hint": "free sample she asked for", "is_gift": true }
  ],
  "delivery_charge": 80,
  "discount_amount": 50,
  "payment_method": "bKash",
  "payment_amount": 80,
  "payment_reference": "8N7A6D5X",
  "order_notes": "Call before delivery",
  "shipping_hint": "Inside Dhaka city",
  "notes": null
}`;

// Field reference shown in the in-app format guide. Kept as data rather
// than hand-written JSX rows so it's easy to keep in sync with
// types/orderImport.ts — and with buildAiInstructions below, which is the
// same contract worded for the AI.
const FIELD_REFERENCE: {
    field: string;
    required: boolean;
    description: string;
}[] = [
    {
        field: "items",
        required: true,
        description:
            "Non-empty array of line items. This is the only required field in the whole payload.",
    },
    {
        field: "items[].sku / items[].product_name",
        required: true,
        description:
            "At least one of the two, per item. SKU gives an exact match; product_name is matched by search — use whatever you're more sure of.",
    },
    {
        field: "items[].customer_hint",
        required: false,
        description:
            "What the customer actually said about this item, quoted or paraphrased. Shown to staff next to the matched product so they can sanity-check it — never matched against anything itself.",
    },
    {
        field: "items[].quantity",
        required: false,
        description: "Defaults to 1 if omitted.",
    },
    {
        field: "items[].unit_price",
        required: false,
        description:
            "Leave this out. The live catalog price is used automatically — a guessed price is more likely to be wrong than stale.",
    },
    {
        field: "items[].is_gift",
        required: false,
        description:
            "true for a free item given to the customer at no charge. Its price is set to 0 on the order.",
    },
    {
        field: "items[].needs_review / items[].review_note",
        required: false,
        description:
            "Flag an item you're unsure about, with a short reason. Surfaced to staff, doesn't block the import.",
    },
    {
        field: "customer.phone / first_name / last_name / email / alternative_phone / street_address / city / map_link / profile / note",
        required: false,
        description:
            "Any subset. phone is used to match an existing customer; unmatched, a new customer is created from whatever fields you provide (map_link, profile and note are only saved on a new customer — an existing one is left untouched).",
    },
    {
        field: "delivery_address.street_address / city",
        required: false,
        description:
            "Only when the order goes somewhere other than the customer's own address (\"send it to my office\"). Otherwise the customer's address is used for delivery too.",
    },
    {
        field: "channel",
        required: false,
        description:
            'Where the order came from, in plain words — e.g. "Facebook", "WhatsApp", "Phone call". Matched by name against the store\'s own channel list; left for staff to pick if it doesn\'t match.',
    },
    {
        field: "delivery_service",
        required: false,
        description:
            'The courier / delivery service, if the conversation names one — e.g. "Pathao". Matched by name against the store\'s own delivery service list; left for staff to pick if it doesn\'t match.',
    },
    {
        field: "delivery_charge",
        required: false,
        description:
            'What the customer is charged for delivery, as a plain number — only if the conversation states it outright ("delivery 80 taka", or 0 for free delivery). If it\'s only implied ("inside Dhaka"), leave it out and use shipping_hint.',
    },
    {
        field: "delivery_cost",
        required: false,
        description:
            "What the store pays the courier — an internal figure a customer chat almost never contains. Leave it out unless it's stated outright.",
    },
    {
        field: "delivery_hint / shipping_hint",
        required: false,
        description:
            'Plain-language context shown next to the Delivery Service / Delivery Charge fields — e.g. "heavier items, may need in-house delivery" or "inside Dhaka city". For anything the conversation only hinted at rather than stated.',
    },
    {
        field: "discount_amount",
        required: false,
        description: "A discount explicitly discussed in the conversation, as a plain number.",
    },
    {
        field: "payment_method / payment_amount / payment_reference",
        required: false,
        description:
            'Only when the customer has ALREADY paid (e.g. sent a bKash payment). Pre-added to the order form\'s payments for staff to confirm or remove. method: cash, card, bKash, Nagad, Rocket or bank transfer. reference: the transaction ID, if given. A promise to pay later / cash on delivery is NOT a payment — leave these out.',
    },
    {
        field: "status",
        required: false,
        description:
            'DRAFT, PENDING or CONFIRMED. Leave it out for a normal order (PENDING). DRAFT is for an order the customer hasn\'t really committed to yet — it reserves no stock until finalized.',
    },
    {
        field: "order_notes",
        required: false,
        description:
            'Instructions that belong on the order itself, seen by whoever packs and delivers it — e.g. "call before delivery", "deliver after 6pm".',
    },
    {
        field: "show_gifts_on_bill",
        required: false,
        description:
            "true only if the customer wants gift items shown on their bill. Usually left out.",
    },
    {
        field: "notes",
        required: false,
        description:
            "Commentary for the person reviewing the import — what you were unsure about, extra context. Shown on the review screen only; never saved on the order (use order_notes for that).",
    },
    {
        field: "order_needs_review / order_review_note",
        required: false,
        description: "Flag the whole order (rather than one item) with a short reason.",
    },
];

// Plain-text version of the same contract, meant to be copy-pasted as-is
// into a conversation with whatever AI assistant is reading the chat log —
// so it knows exactly what shape to produce without us having to explain
// how that assistant should read the conversation itself (out of scope
// here; this only defines the output contract). Takes the store's live
// channel / delivery-service names so the AI can pick exact ones instead
// of guessing — pass empty lists (e.g. if the fetch failed) and the
// instructions simply fall back to plain wording.
const buildAiInstructions = (
    channelNames: string[],
    deliveryServiceNames: string[],
) => {
    const namesLine = (label: string, names: string[]) =>
        names.length > 0
            ? `\n  This store's ${label} (use one of these exactly, or omit if none fits): ${names.join(", ")}`
            : "";

    return `You are extracting a customer order from a chat conversation so it can be imported into our order system.

You should already have our current product catalog and customer list (exported as CSV from the store's admin panel) earlier in this conversation. Use them: prefer an exact sku from the catalog over guessing a product_name, and prefer an exact phone from the customer list when the chat identifies the customer. If you don't have those files, ask for them before proceeding — matches are far more reliable with them than without.

Output ONLY a single JSON object — no prose, no markdown code fences — matching this schema.

GROUND RULES
- Everything you fill in lands PRE-FILLED on an order form that a staff member reviews and edits before anything is saved. So do fill in every value the conversation actually states — including delivery charge, discount and payments — rather than leaving them for staff to retype.
- But never guess or calculate a value the conversation doesn't state. If it isn't there, leave the field out (or null). A missing field is fine; a wrong one is not.
- Amounts are plain JSON numbers in taka: 80, not "৳80" or "80 tk".

REQUIRED
- items: non-empty array of line items. Every other field in this schema is optional.
  Each item needs AT LEAST ONE of:
  - sku (string): exact product SKU, if you know it
  - product_name (string): the product name/description as best you can tell
  Each item may also include:
  - customer_hint (string): what the customer actually said about this item (quote or paraphrase) — shown to staff next to the matched product so they can sanity-check it
  - quantity (number): defaults to 1 if omitted
  - is_gift (boolean): true if this is a free item given to the customer at no charge
  - needs_review (boolean) + review_note (string): flag an item you're unsure about, with why
  - do NOT include unit_price — the live catalog price is used automatically

OPTIONAL — CUSTOMER & DELIVERY
- customer: { phone, first_name, last_name, email, alternative_phone, street_address, city, map_link, profile, note } — any subset; phone is used to match an existing customer. map_link is a Google Maps / location link the customer shared. profile is the customer's social profile link (e.g. the Facebook or Instagram profile they messaged from) or a short line of general info. note is something worth remembering about the customer themselves (not this order).
- delivery_address: { street_address, city } — ONLY if the order should be delivered somewhere other than the customer's own address (e.g. "send it to my office at ..."). Otherwise omit it; the customer's address is used for delivery too.
- channel (string): where the order came from in plain words, e.g. "Facebook", "WhatsApp", "Phone call"${namesLine("order channels", channelNames)}
- delivery_service (string): the courier / delivery service, if the conversation names one${namesLine("delivery services", deliveryServiceNames)}
- delivery_charge (number): what the customer is charged for delivery — ONLY if the conversation states it outright (e.g. "delivery charge 80", or 0 for "free delivery"). If it's only implied (e.g. "inside Dhaka"), do not work it out yourself; put that in shipping_hint instead.
- delivery_cost (number): what WE pay the courier. Customers almost never mention this — omit it unless it's stated outright.
- delivery_hint (string): plain-language delivery context that isn't a specific courier, e.g. "heavier items, may need in-house delivery"
- shipping_hint (string): plain-language delivery-charge/area context, e.g. "inside Dhaka city"

OPTIONAL — MONEY & ORDER
- discount_amount (number): a discount explicitly discussed
- payment_method (string) + payment_amount (number) + payment_reference (string): ONLY if the customer has ALREADY paid — e.g. they sent a bKash payment or a transaction ID/screenshot. payment_method is one of: cash, card, bKash, Nagad, Rocket, bank transfer. payment_reference is the transaction ID, if given. A promise to pay later, or cash on delivery, is NOT a payment — leave all three out.
- status (string): "DRAFT", "PENDING" or "CONFIRMED". Omit for a normal order. Use "DRAFT" only if the customer hasn't really committed to buying yet.
- order_notes (string): instructions that belong ON the order itself, seen by whoever packs and delivers it — e.g. "call before delivery", "deliver after 6pm", "gift wrap please"
- show_gifts_on_bill (boolean): only if the customer wants gift items shown on their bill. Usually omit.
- notes (string): commentary for the person REVIEWING this import — what you were unsure about, extra context. It is not saved on the order; put anything that belongs on the order in order_notes.
- order_needs_review (boolean) + order_review_note (string): flag the whole order, with why

EXAMPLE
${SAMPLE_PLACEHOLDER}`;
};

export const OrderImportDialog: React.FC<OrderImportDialogProps> = ({
    open,
    onClose,
    onResolved,
}) => {
    const [step, setStep] = useState<"paste" | "review">("paste");
    const [rawJson, setRawJson] = useState("");
    const [parseError, setParseError] = useState<string | null>(null);
    const [parseWarnings, setParseWarnings] = useState<string[]>([]);
    const [payload, setPayload] = useState<OrderImportPayload | null>(null);
    const [resolving, setResolving] = useState(false);

    const [itemRows, setItemRows] = useState<ItemRowState[]>([]);
    const [customerResolution, setCustomerResolution] =
        useState<CustomerResolution | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
        null,
    );
    const [resolvedChannel, setResolvedChannel] = useState<
        OrderChannelSimple | undefined
    >(undefined);
    const [resolvedService, setResolvedService] = useState<
        DeliveryServiceSimple | undefined
    >(undefined);
    const [guideOpen, setGuideOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open) {
            setStep("paste");
            setRawJson("");
            setParseError(null);
            setParseWarnings([]);
            setPayload(null);
            setItemRows([]);
            setCustomerResolution(null);
            setSelectedCustomer(null);
            setResolvedChannel(undefined);
            setResolvedService(undefined);
            setGuideOpen(false);
            setCopied(false);
        }
    }, [open]);

    const handleCopyInstructions = async () => {
        // Embed the store's real channel / delivery-service names so the AI
        // picks exact ones. A failed fetch just falls back to plain wording.
        const [channels, services] = await Promise.all([
            orderChannelService
                .getOrderChannelsSimple(true)
                .catch(() => [] as OrderChannelSimple[]),
            deliveryService
                .getDeliveryServicesSimple(true)
                .catch(() => [] as DeliveryServiceSimple[]),
        ]);
        try {
            await navigator.clipboard.writeText(
                buildAiInstructions(
                    channels.map((c) => c.name),
                    services.map((s) => s.name),
                ),
            );
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy import instructions:", err);
        }
    };

    const handleResolve = async () => {
        const result = parseOrderImportPayload(rawJson);
        if ("error" in result) {
            setParseError(result.error);
            return;
        }
        setParseError(null);
        setParseWarnings(result.warnings);
        const data = result.payload;
        setPayload(data);
        setStep("review");
        setResolving(true);

        try {
            const [channels, services, productResolutions, customerRes] =
                await Promise.all([
                    orderChannelService
                        .getOrderChannelsSimple(true)
                        .catch(() => [] as OrderChannelSimple[]),
                    deliveryService
                        .getDeliveryServicesSimple(true)
                        .catch(() => [] as DeliveryServiceSimple[]),
                    Promise.all(data.items.map((item) => resolveProduct(item))),
                    resolveCustomer(data.customer),
                ]);

            setResolvedChannel(resolveNamed(data.channel, channels));
            setResolvedService(resolveNamed(data.delivery_service, services));
            setItemRows(
                data.items.map((item, idx) => {
                    const resolution = productResolutions[idx];
                    return {
                        item,
                        resolution,
                        selectedProduct:
                            resolution.status === "matched"
                                ? resolution.product
                                : null,
                        skipped: false,
                        editing: false,
                    };
                }),
            );
            setCustomerResolution(customerRes);
            if (customerRes.status === "matched") {
                setSelectedCustomer(customerRes.customer);
            }
        } finally {
            setResolving(false);
        }
    };

    const updateRow = (index: number, patch: Partial<ItemRowState>) => {
        setItemRows((rows) =>
            rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
        );
    };

    const canContinue =
        !resolving &&
        itemRows.length > 0 &&
        itemRows.every((r) => r.selectedProduct || r.skipped);

    // A mentioned payment only becomes a real pre-added payment row when
    // it's complete and unambiguous — otherwise it's left out and the
    // review screen says why, so staff can add it by hand.
    const paymentPlan: PaymentPlan = (() => {
        if (!payload) return { kind: "none" };
        const { payment_method, payment_amount, payment_reference } = payload;
        if (!payment_method && payment_amount == null && !payment_reference) {
            return { kind: "none" };
        }
        if (!payment_amount || payment_amount <= 0) {
            return { kind: "skipped", reason: "no amount was given" };
        }
        const method = resolvePaymentMethod(payment_method);
        if (!method) {
            return {
                kind: "skipped",
                reason: payment_method
                    ? `"${payment_method}" isn't a payment method this form can pre-add (cash, card, bKash, Nagad, Rocket, bank transfer)`
                    : "no payment method was given",
            };
        }
        if (payload.status === "DRAFT") {
            return {
                kind: "skipped",
                reason: "a draft order can't hold a payment — finalize it first, then record it",
            };
        }
        return {
            kind: "ready",
            method,
            amount: payment_amount,
            reference: payment_reference,
        };
    })();

    const handleContinue = () => {
        if (!payload || !canContinue) return;

        const items: OrderFormPrefill["items"] = [];
        const itemHints: Record<number, string> = {};

        itemRows.forEach((row) => {
            if (!row.selectedProduct) return;
            if (row.item.customer_hint) {
                itemHints[items.length] = row.item.customer_hint;
            }
            const quantity = row.item.quantity && row.item.quantity > 0 ? row.item.quantity : 1;
            items.push({
                product_id: row.selectedProduct.product_id,
                quantity,
                // A gift is free by definition — the form forces this too.
                // A price the chat stated wins; otherwise today's price for
                // this quantity (sale, campaign or combo price).
                unit_price: row.item.is_gift
                    ? 0
                    : (row.item.unit_price ?? unitPriceFor(row.selectedProduct, quantity)),
                ...(row.item.is_gift ? { is_gift: true } : {}),
            });
        });

        // `order_notes` (things that belong ON the order) fills the form's
        // Notes field. `notes` — the AI's own commentary for whoever is
        // reviewing the import — stays on this review screen only, so it
        // never clutters a field nobody asked to save it into.
        const customerStreet = payload.customer?.street_address || "";
        const customerCity = payload.customer?.city || "";
        const prefill: OrderFormPrefill = {
            ...(selectedCustomer
                ? { existing_customer: selectedCustomer }
                : {
                      new_customer: {
                          first_name: payload.customer?.first_name || "",
                          last_name: payload.customer?.last_name || undefined,
                          email: payload.customer?.email || undefined,
                          phone: payload.customer?.phone || undefined,
                          alternative_phone:
                              payload.customer?.alternative_phone || undefined,
                          street_address: customerStreet,
                          city: customerCity,
                          map_link: payload.customer?.map_link || undefined,
                          profile: payload.customer?.profile || undefined,
                          note: payload.customer?.note || undefined,
                      },
                  }),
            items,
            shipping_address:
                payload.delivery_address?.street_address || customerStreet,
            shipping_city: payload.delivery_address?.city || customerCity,
            order_channel_id: resolvedChannel?.order_channel_id,
            delivery_service_id: resolvedService?.delivery_service_id,
            shipping_amount: payload.delivery_charge ?? 0,
            delivery_cost: payload.delivery_cost,
            discount_amount: payload.discount_amount,
            status: payload.status,
            show_gifts_on_bill: payload.show_gifts_on_bill,
            notes: payload.order_notes,
            payments:
                paymentPlan.kind === "ready"
                    ? [
                          {
                              amount: paymentPlan.amount,
                              payment_method: paymentPlan.method,
                              reference_number: paymentPlan.reference,
                          },
                      ]
                    : undefined,
            delivery_hint: payload.delivery_hint,
            shipping_hint: payload.shipping_hint,
            item_hints: itemHints,
        };

        onResolved(prefill);
    };

    const statusIcon = (row: ItemRowState) => {
        if (row.skipped) return <Cancel fontSize="small" color="disabled" />;
        if (row.selectedProduct)
            return <CheckCircle fontSize="small" color="success" />;
        if (row.resolution.status === "ambiguous")
            return <Warning fontSize="small" color="warning" />;
        return <Cancel fontSize="small" color="error" />;
    };

    // The resolved product's real title is what staff actually recognize —
    // SKUs are a backend lookup key, not something anyone reads at a
    // glance. Prefer it once matched; fall back to whatever the JSON gave
    // us (name, then SKU) only while still unresolved.
    const itemLabel = (row: ItemRowState) =>
        row.selectedProduct?.name ||
        row.item.product_name ||
        row.item.sku ||
        "Unnamed item";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Import Order from Chat
                <Box sx={{ flexGrow: 1 }} />
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {step === "paste" && (
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Paste the JSON produced from a customer chat conversation.
                                Only the product items are required — everything else
                                (customer, delivery service and charge, discount, payment,
                                notes…) is optional and will be left blank for you to fill
                                in if missing.
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<HelpOutline />}
                                onClick={() => setGuideOpen(true)}
                                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                            >
                                Format guide
                            </Button>
                        </Box>
                        <TextField
                            fullWidth
                            multiline
                            rows={14}
                            value={rawJson}
                            onChange={(e) => setRawJson(e.target.value)}
                            placeholder={SAMPLE_PLACEHOLDER}
                            sx={{ fontFamily: "monospace" }}
                            InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.85rem" } }}
                        />
                        {parseError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {parseError}
                            </Alert>
                        )}
                    </Box>
                )}

                {step === "review" && (
                    <Box>
                        {resolving ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2">
                                    Matching items, customer, and channel against live data...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Customer */}
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Customer
                                </Typography>
                                {customerResolution?.status === "matched" && selectedCustomer ? (
                                    <Alert severity="success" sx={{ mb: 1 }}>
                                        Matched existing customer: {selectedCustomer.first_name}{" "}
                                        {selectedCustomer.last_name || ""} (
                                        {selectedCustomer.phone})
                                    </Alert>
                                ) : (
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                        No existing customer matched — a new one will be created
                                        {payload?.customer?.first_name
                                            ? ` for ${payload.customer.first_name}`
                                            : ""}
                                        . Search below if the AI missed an existing match.
                                    </Alert>
                                )}
                                <CustomerSearchAutocomplete
                                    value={selectedCustomer}
                                    onChange={setSelectedCustomer}
                                    label="Override matched customer (optional)"
                                />

                                <Divider sx={{ my: 2 }} />

                                {/* What will be pre-filled on the order form */}
                                {parseWarnings.map((w, i) => (
                                    <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                                        {w}
                                    </Alert>
                                ))}
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                                    {payload?.channel &&
                                        (resolvedChannel ? (
                                            <Chip
                                                size="small"
                                                color="success"
                                                label={`Channel: ${resolvedChannel.name}`}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                color="warning"
                                                label={`Channel "${payload.channel}" not found — pick manually`}
                                            />
                                        ))}
                                    {payload?.delivery_service &&
                                        (resolvedService ? (
                                            <Chip
                                                size="small"
                                                color="success"
                                                label={`Delivery service: ${resolvedService.name}`}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                color="warning"
                                                label={`Delivery service "${payload.delivery_service}" not found — pick manually`}
                                            />
                                        ))}
                                    {payload?.delivery_charge != null && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            label={`Delivery charge: ৳${payload.delivery_charge}`}
                                        />
                                    )}
                                    {payload?.delivery_cost != null && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            label={`Delivery cost: ৳${payload.delivery_cost}`}
                                        />
                                    )}
                                    {!!payload?.discount_amount && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            label={`Discount: ৳${payload.discount_amount}`}
                                        />
                                    )}
                                    {payload?.status && (
                                        <Chip
                                            size="small"
                                            color={payload.status === "DRAFT" ? "default" : "success"}
                                            label={`Status: ${payload.status}`}
                                        />
                                    )}
                                </Box>
                                {payload?.delivery_address && (
                                    <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                                        Delivering to:{" "}
                                        {[
                                            payload.delivery_address.street_address,
                                            payload.delivery_address.city,
                                        ]
                                            .filter(Boolean)
                                            .join(", ")}{" "}
                                        (differs from the customer&apos;s own address)
                                    </Alert>
                                )}
                                {payload?.order_notes && (
                                    <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                                        Order notes: {payload.order_notes}
                                    </Alert>
                                )}
                                {paymentPlan.kind === "ready" && (
                                    <Alert severity="warning" sx={{ mb: 1 }}>
                                        A payment of ৳{paymentPlan.amount} via{" "}
                                        {PAYMENT_METHOD_LABELS[paymentPlan.method]}
                                        {paymentPlan.reference
                                            ? ` (ref ${paymentPlan.reference})`
                                            : ""}{" "}
                                        will be pre-added to the order. Keep it only if that
                                        money has actually been received — you can remove it
                                        in the form.
                                    </Alert>
                                )}
                                {paymentPlan.kind === "skipped" && (
                                    <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                                        A payment was mentioned but not pre-added:{" "}
                                        {paymentPlan.reason}. Add it by hand in the form if it
                                        applies.
                                    </Alert>
                                )}
                                {payload?.delivery_hint && (
                                    <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                                        Delivery hint: {payload.delivery_hint}
                                    </Alert>
                                )}
                                {payload?.shipping_hint && (
                                    <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                                        Shipping hint: {payload.shipping_hint}
                                    </Alert>
                                )}
                                {payload?.order_needs_review && (
                                    <Alert severity="warning" sx={{ mb: 1 }}>
                                        Order flagged for review
                                        {payload.order_review_note
                                            ? `: ${payload.order_review_note}`
                                            : ""}
                                    </Alert>
                                )}
                                {payload?.notes && (
                                    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                                        Notes from the conversation: {payload.notes}
                                    </Alert>
                                )}
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ display: "block", mb: 2 }}
                                >
                                    Nothing is saved yet — the order form opens next with
                                    everything above filled in, for you to review and edit
                                    before creating the order.
                                </Typography>

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Items ({itemRows.length})
                                </Typography>
                                {itemRows.map((row, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            p: 1.5,
                                            mb: 1.5,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                            {statusIcon(row)}
                                            {row.selectedProduct && !row.skipped && (
                                                <ProductThumbnail
                                                    images={row.selectedProduct.images}
                                                    alt={row.selectedProduct.name}
                                                    size={40}
                                                />
                                            )}
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {itemLabel(row)}
                                                </Typography>
                                                {row.item.customer_hint && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Customer said: &ldquo;{row.item.customer_hint}&rdquo;
                                                    </Typography>
                                                )}
                                                {row.item.needs_review && (
                                                    <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0 }}>
                                                        Flagged for review
                                                        {row.item.review_note ? `: ${row.item.review_note}` : ""}
                                                    </Alert>
                                                )}
                                            </Box>
                                            {row.selectedProduct && !row.skipped && (
                                                <Button
                                                    size="small"
                                                    startIcon={<EditIcon />}
                                                    onClick={() =>
                                                        updateRow(index, { editing: !row.editing })
                                                    }
                                                >
                                                    Change
                                                </Button>
                                            )}
                                            {!row.skipped ? (
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    onClick={() =>
                                                        updateRow(index, {
                                                            skipped: true,
                                                            selectedProduct: null,
                                                        })
                                                    }
                                                >
                                                    Skip
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    startIcon={<Undo />}
                                                    onClick={() => updateRow(index, { skipped: false })}
                                                >
                                                    Undo
                                                </Button>
                                            )}
                                        </Box>

                                        {row.skipped && (
                                            <Typography variant="caption" color="text.secondary">
                                                This item will not be added to the order.
                                            </Typography>
                                        )}

                                        {!row.skipped && row.selectedProduct && (
                                            <Box sx={{ mt: 0.5, ml: 4, display: "flex", alignItems: "baseline", gap: 1 }}>
                                                <Typography variant="body2" color="success.main">
                                                    ৳{row.selectedProduct.selling_price}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.disabled"
                                                    sx={{ fontFamily: "monospace" }}
                                                >
                                                    SKU {row.selectedProduct.sku}
                                                </Typography>
                                            </Box>
                                        )}

                                        {!row.skipped && row.resolution.status === "ambiguous" && !row.selectedProduct && (
                                            <Box sx={{ mt: 1, ml: 4, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                {row.resolution.candidates.map((c) => (
                                                    <Tooltip key={c.product_id} title={`SKU ${c.sku}`}>
                                                        <Chip
                                                            avatar={
                                                                <Avatar src={getProductThumbnailUrl(c.images)}>
                                                                    <Inventory2Outlined fontSize="small" />
                                                                </Avatar>
                                                            }
                                                            label={c.name}
                                                            size="small"
                                                            onClick={() =>
                                                                updateRow(index, {
                                                                    selectedProduct: c,
                                                                    editing: false,
                                                                })
                                                            }
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        )}

                                        {!row.skipped && (!row.selectedProduct || row.editing) && (
                                            <Box sx={{ mt: 1.5, ml: 4 }}>
                                                <ProductSearchAutocomplete
                                                    value={null}
                                                    onChange={(product) => {
                                                        if (product) {
                                                            updateRow(index, {
                                                                selectedProduct: product as Product,
                                                                editing: false,
                                                            });
                                                        }
                                                    }}
                                                    label="Search for the right product"
                                                    initialSearchTerm={row.item.sku || row.item.product_name}
                                                    secondaryLine="sku"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {step === "paste" ? (
                    <>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleResolve}
                            disabled={!rawJson.trim()}
                        >
                            Resolve &amp; Review
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setStep("paste")}>Back</Button>
                        <Button
                            variant="contained"
                            onClick={handleContinue}
                            disabled={!canContinue}
                        >
                            Continue to Order Form
                        </Button>
                    </>
                )}
            </DialogActions>

            <Dialog
                open={guideOpen}
                onClose={() => setGuideOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    JSON Import Format
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton onClick={() => setGuideOpen(false)} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This is the format an AI assistant reading a customer chat
                        conversation should produce, for pasting into the box on the
                        previous screen. Only <strong>items</strong> is required —
                        everything else is optional and safe to leave out.
                    </Typography>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Before you start, export the product catalog (Products →
                        Export CSV) and the customer list (Customers → Export CSV)
                        and share both files with your AI assistant along with the
                        chat log. Without them it can only guess at SKUs and
                        product/customer names from memory — with them, matches on
                        the review screen come back exact far more often.
                    </Alert>

                    <Box sx={{ mb: 2 }}>
                        {FIELD_REFERENCE.map((row, idx) => (
                            <Box
                                key={row.field}
                                sx={{
                                    py: 1,
                                    borderTop: idx === 0 ? "none" : "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexWrap: "wrap",
                                        mb: 0.25,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: "monospace", fontWeight: "bold" }}
                                    >
                                        {row.field}
                                    </Typography>
                                    {row.required ? (
                                        <Chip label="Required" size="small" color="primary" />
                                    ) : (
                                        <Chip label="Optional" size="small" variant="outlined" />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {row.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Example
                    </Typography>
                    <Box
                        component="pre"
                        sx={{
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            p: 1.5,
                            fontSize: "0.78rem",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            mb: 1,
                        }}
                    >
                        {SAMPLE_PLACEHOLDER}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGuideOpen(false)}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyInstructions}
                    >
                        {copied ? "Copied!" : "Copy instructions for AI"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};
